/**
 * Single access point to the Zebra Browser Print app installed on the tablet
 * (http://127.0.0.1:9100), through the SDK loaded by public/index.html.
 *
 * Every call has a time limit and fails with a BrowserPrintError whose `code`
 * tells what went wrong, so the UI can name the problem.
 */

export const BROWSER_PRINT_ERRORS = {
  ABSENT: "BP_ABSENT", // SDK not loaded, or the app refused the request (closed)
  NO_RESPONSE: "BP_NO_RESPONSE", // the app did not answer in time
  NO_PRINTER: "NO_PRINTER", // the search found no printer
  PRINTER_NO_RESPONSE: "PRINTER_NO_RESPONSE" // a printer did not answer, or answered garbage
}

const TIMEOUTS_MS = {
  search: 8000,
  name: 2000,
  status: 5000,
  send: 10000
}

const STX = String.fromCharCode(2)
const ETX = String.fromCharCode(3)

export class BrowserPrintError extends Error {
  constructor(code, message) {
    super(message || code)
    this.name = "BrowserPrintError"
    this.code = code
  }
}

// Rejects with `code` when `promise` has not settled within `ms`.
// The SDK cannot abort a request: a late answer is simply ignored.
function withTimeout(promise, ms, code) {
  let timer
  const timeout = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new BrowserPrintError(code)), Math.max(0, ms))
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function getSdk() {
  if (!window?.BrowserPrint) throw new BrowserPrintError(BROWSER_PRINT_ERRORS.ABSENT, "BrowserPrint SDK not found on window")
  return window.BrowserPrint
}

// Rebuild a SDK Device from a printer of the list, which may be a plain copy
function toDevice(printer) {
  const sdk = getSdk()
  return new sdk.Device(printer)
}

/** BrowserPrint.getLocalDevices returns either an array or { printer: [...] } */
function normalizePrinterList(result) {
  if (!result) return []
  if (Array.isArray(result)) return result
  if (result.printer && Array.isArray(result.printer)) return result.printer
  return []
}

/**
 * Ask Browser Print for the printers it knows, network and USB.
 * Throws NO_PRINTER when the list is empty.
 */
export async function findPrinters() {
  const sdk = getSdk()
  const result = await withTimeout(
    new Promise((resolve, reject) => {
      sdk.getLocalDevices(resolve, () => reject(new BrowserPrintError(BROWSER_PRINT_ERRORS.ABSENT)), "printer")
    }),
    TIMEOUTS_MS.search,
    BROWSER_PRINT_ERRORS.NO_RESPONSE
  )

  const printers = normalizePrinterList(result)
    .filter(device => device?.uid && device.deviceType === "printer" && ["network", "usb"].includes(device.connection))

  if (printers.length === 0) throw new BrowserPrintError(BROWSER_PRINT_ERRORS.NO_PRINTER)
  return printers
}

function sendOnce(device, data) {
  return new Promise((resolve, reject) => {
    device.send(data, resolve, () => reject(new BrowserPrintError(BROWSER_PRINT_ERRORS.PRINTER_NO_RESPONSE)))
  })
}

function readOnce(device) {
  return new Promise((resolve, reject) => {
    device.read(resolve, () => reject(new BrowserPrintError(BROWSER_PRINT_ERRORS.PRINTER_NO_RESPONSE)))
  })
}

/**
 * Read the printer's answer, possibly in several chunks, until `isComplete(text)`.
 * The SDK's own loop stops at the first occurrence of a single string, which is
 * not enough for ~HS (3 frames) or a quoted SGD value.
 * On timeout, the error carries what was read so far in `partial`.
 */
async function readUntil(device, isComplete, deadline) {
  let text = ""
  try {
    while (Date.now() < deadline) {
      const chunk = await withTimeout(readOnce(device), deadline - Date.now(), BROWSER_PRINT_ERRORS.PRINTER_NO_RESPONSE)
      text += typeof chunk === "string" ? chunk : ""
      if (isComplete(text)) return text
      if (!chunk) await wait(50)
    }
    throw new BrowserPrintError(BROWSER_PRINT_ERRORS.PRINTER_NO_RESPONSE)
  } catch (e) {
    e.partial = text
    throw e
  }
}

// Browser Print keeps one read buffer per printer: one exchange at a time per printer,
// otherwise a name read and a status read take each other's answers
const printerQueues = {}
function runExclusive(uid, task) {
  const run = (printerQueues[uid] || Promise.resolve()).then(task)
  printerQueues[uid] = run.catch(() => {})
  return run
}

// Send a command, then read its answer, within `ms` from the moment the printer is free
function query(printer, command, isComplete, ms) {
  return runExclusive(printer?.uid, async () => {
    const device = toDevice(printer)
    const deadline = Date.now() + ms
    await withTimeout(sendOnce(device, command), ms, BROWSER_PRINT_ERRORS.PRINTER_NO_RESPONSE)
    return readUntil(device, isComplete, deadline)
  })
}

/**
 * Read the printer's friendly name with a direct SGD query, without Zebra.Printer
 * (its constructor polls the whole configuration every second until it answers).
 * Resolves "" when the answer holds no name.
 */
export async function readFriendlyName(printer) {
  const answer = await query(
    printer,
    "! U1 getvar \"device.friendly_name\"\r\n",
    text => text.split("\"").length > 2, // opening and closing quotes received
    TIMEOUTS_MS.name
  )
  const match = answer.match(/"([^"]*)"/)
  return match ? match[1].trim() : ""
}

/**
 * Find the 3 strings of a ~HS answer (each framed by STX…ETX), as field lists.
 * Frames with less than 3 fields before string 1 are skipped: a 3rd string ("xxxx,y")
 * left in the buffer by a previous answer must not be taken for string 1.
 * Returns null while strings 1 and 2 are not both received.
 */
function findHostStatusFrames(text) {
  const frames = text
    .split(ETX)
    .slice(0, -1) // the last part has no ETX yet
    .filter(part => part.includes(STX))
    .map(part => part.slice(part.lastIndexOf(STX) + 1).split(","))

  const start = frames.findIndex((frame, i) => frame.length >= 3 && frames[i + 1]?.length >= 4)
  if (start < 0) return null
  return { first: frames[start], second: frames[start + 1], complete: frames.length > start + 2 }
}

/**
 * Decode a ~HS answer with the same flags as Zebra.Printer.Status in the SDK
 * (characters 5, 7, 43 and 45 of the raw answer), read by field so that it does
 * not depend on the line breaks between frames:
 *  - string 1: field 2 = paper out, field 3 = paused
 *  - string 2: field 3 = head open, field 4 = ribbon out
 */
function decodeHostStatus(frames) {
  if (!frames) throw new BrowserPrintError(BROWSER_PRINT_ERRORS.PRINTER_NO_RESPONSE, "Invalid ~HS answer")
  const { first, second } = frames
  return {
    paperOut: first[1] === "1",
    paused: first[2] === "1",
    headOpen: second[2] === "1",
    ribbonOut: second[3] === "1"
  }
}

/**
 * Read the printer status (~HS). Head and ribbon flags are in string 2; the read waits
 * for string 3 too, so that nothing is left in the buffer for the next exchange.
 * If string 3 never comes, strings 1 and 2 are enough.
 */
export async function readStatus(printer) {
  try {
    const raw = await query(printer, "~HS\r\n", text => !!findHostStatusFrames(text)?.complete, TIMEOUTS_MS.status)
    return decodeHostStatus(findHostStatusFrames(raw))
  } catch (e) {
    const frames = e?.partial ? findHostStatusFrames(e.partial) : null
    if (frames) return decodeHostStatus(frames)
    throw e
  }
}

/** Send ZPL to the printer. Resolves once Browser Print has accepted it, not once it is printed */
export function sendZpl(printer, zpl) {
  return runExclusive(printer?.uid, () => withTimeout(sendOnce(toDevice(printer), zpl), TIMEOUTS_MS.send, BROWSER_PRINT_ERRORS.PRINTER_NO_RESPONSE))
}
